import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatMoney } from '@teentack/shared';
import {
  useMyApplications,
  useWithdrawApplication,
} from '@/features/applications/use-applications';
import { useMyAssignments, useRespondToAssignment } from '@/features/assignments/use-assignments';

import { useMyConsentRequests } from '@/features/consent/use-consent';
import { TeenShell } from '@/features/teen/TeenShell';
import { ErrorNote, InfoNote } from '@/components/Form';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { WalletIcon } from '@/components/ui/Icons';
import { MessageButton } from '@/features/messaging/MessageButton';
import { WorksitePanel } from '@/features/worksite/WorksitePanel';
import { ReportButton } from '@/features/safety/ReportButton';
import { useCompleteAssignment } from '@/features/assignments/use-assignments';
import { presentApiError } from '@/lib/form-errors';

const TABS = ['assignments', 'applications', 'consent'] as const;
type Tab = (typeof TABS)[number];

const TAB_LABELS: Record<Tab, string> = {
  assignments: 'My jobs',
  applications: 'Applications',
  consent: 'Permissions',
};

const TONES: Record<string, BadgeTone> = {
  SUBMITTED: 'brand',
  SHORTLISTED: 'accent',
  ACCEPTED: 'safe',
  DECLINED: 'neutral',
  ASSIGNED: 'sun',
  IN_PROGRESS: 'brand',
  COMPLETED: 'safe',
  APPROVED: 'safe',
  PENDING: 'sun',
};

function label(value: string): string {
  return value.charAt(0) + value.slice(1).toLowerCase().replace(/_/g, ' ');
}

/**
 * Everything the teen has in flight, in one place. [Teen steps 8, 10, 13-14, 20]
 *
 * The assignment tab leads because it is the only one carrying an action the
 * teen must take: a job offered to them and waiting on their yes.
 */
export function MyWorkPage() {
  const [tab, setTab] = useState<Tab>('assignments');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const assignments = useMyAssignments({ pageSize: 50 }, tab === 'assignments');
  const applications = useMyApplications({ pageSize: 50 }, tab === 'applications');
  const consents = useMyConsentRequests({ pageSize: 50 }, tab === 'consent');

  const respond = useRespondToAssignment();
  const complete = useCompleteAssignment();
  const withdraw = useWithdrawApplication();

  const waiting = assignments.data?.items.filter((a) => a.status === 'ASSIGNED').length ?? 0;

  async function run(action: () => Promise<unknown>, success: string): Promise<void> {
    setError(null);
    setNotice(null);
    try {
      await action();
      setNotice(success);
    } catch (caught) {
      setError(presentApiError(caught, 'Something went wrong').message);
    }
  }

  return (
    <TeenShell
      title="My work"
      description="Jobs you have been offered, applications in flight, and the permissions behind them."
    >
      <div className="mb-6 flex flex-wrap gap-2">
        {TABS.map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setTab(value)}
            aria-pressed={tab === value}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-xs font-bold transition ${
              tab === value ? 'bg-ink text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
            }`}
          >
            {TAB_LABELS[value]}
            {value === 'assignments' && waiting > 0 && (
              <span className="rounded-full bg-red-500 px-1.5 text-[0.65rem] text-white">
                {waiting}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="mb-4 space-y-3">
        <ErrorNote message={error} />
        {notice && <InfoNote>{notice}</InfoNote>}
      </div>

      {/* --- Assignments: the only tab with a decision to make --------------- */}
      {tab === 'assignments' && (
        <ul className="space-y-4">
          {assignments.data?.items.map((assignment) => (
            <li
              key={assignment.id}
              className="rounded-2xl border border-line bg-white p-5 shadow-soft sm:p-6"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 className="text-base font-bold text-ink">{assignment.job.title}</h2>
                  <p className="mt-1 flex flex-wrap items-center gap-x-3 text-xs text-slate-500">
                    <span>{assignment.job.posterName}</span>
                    <span className="inline-flex items-center gap-1 font-bold text-ink">
                      <WalletIcon className="size-3.5 text-safe-600" />
                      {formatMoney(assignment.agreedPay.amountMinor, assignment.agreedPay.currency)}
                    </span>
                  </p>
                </div>
                <Badge tone={TONES[assignment.status] ?? 'neutral'}>
                  {label(assignment.status)}
                </Badge>
              </div>

              {/* Held back until assignment, so this is the first sight of it. */}
              {assignment.instructions && assignment.status !== 'ASSIGNED' && (
                <div className="mt-4 rounded-xl border border-brand-200 bg-brand-50 px-4 py-3">
                  <p className="text-xs font-bold uppercase tracking-wide text-brand-700">
                    From {assignment.job.posterName}
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-brand-900">
                    {assignment.instructions}
                  </p>
                </div>
              )}

              {/* Once you have the job, you need to be able to ask about it. */}
              <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-line pt-4">
                <MessageButton
                  jobId={assignment.job.id}
                  personId={assignment.job.posterId}
                  label="Message the employer"
                />
                <ReportButton
                  personId={assignment.job.posterId}
                  personName={assignment.job.posterName}
                  jobId={assignment.job.id}
                />
              </div>

              {/* The teen's own end of the completion loop. [Teen steps 16, 18] */}
              {assignment.status === 'ACCEPTED' && (
                <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-line pt-4">
                  <p className="flex-1 text-sm text-slate-600">
                    Finished? Tell the employer so they can sign it off and pay you.
                  </p>
                  <Button
                    variant="success"
                    disabled={complete.isPending}
                    onClick={() =>
                      void run(
                        () => complete.mutateAsync(assignment.id),
                        'Marked done. The employer will review it.',
                      )
                    }
                  >
                    {complete.isPending ? 'Sending…' : 'Mark as done'}
                  </Button>
                </div>
              )}

              {assignment.status === 'COMPLETED' && (
                <p className="mt-4 rounded-xl border border-sun-200 bg-sun-50 px-4 py-3 text-sm font-medium text-sun-700">
                  Waiting for {assignment.job.posterName} to approve your work.
                </p>
              )}

              {assignment.status === 'APPROVED' && (
                <p className="mt-4 rounded-xl border border-safe-200 bg-safe-50 px-4 py-3 text-sm font-medium text-safe-700">
                  {assignment.paymentStatus === 'RELEASED'
                    ? 'Approved and paid. Nice work.'
                    : 'Approved. Payment is on its way.'}
                </p>
              )}

              {/* Progress and location only make sense on live work. */}
              {['ACCEPTED', 'IN_PROGRESS', 'COMPLETED'].includes(assignment.status) && (
                <details className="mt-4 border-t border-line pt-4">
                  <summary className="cursor-pointer text-sm font-semibold text-brand-600">
                    Progress and location
                  </summary>
                  <div className="mt-4">
                    <WorksitePanel assignmentId={assignment.id} canPost />
                  </div>
                </details>
              )}

              {assignment.status === 'ASSIGNED' && (
                <div className="mt-4 flex flex-wrap gap-2 border-t border-line pt-4">
                  <p className="w-full text-sm text-slate-600">
                    You have been offered this job. Confirm to take it.
                  </p>
                  <Button
                    variant="success"
                    disabled={respond.isPending}
                    onClick={() =>
                      void run(
                        () =>
                          respond.mutateAsync({ assignmentId: assignment.id, response: 'ACCEPT' }),
                        'Confirmed. The job poster has been told.',
                      )
                    }
                  >
                    Accept
                  </Button>
                  <Button
                    variant="outline"
                    disabled={respond.isPending}
                    onClick={() =>
                      void run(
                        () =>
                          respond.mutateAsync({ assignmentId: assignment.id, response: 'DECLINE' }),
                        'Declined. The place goes back to the job poster.',
                      )
                    }
                  >
                    Decline
                  </Button>
                </div>
              )}
            </li>
          ))}
          {assignments.data?.items.length === 0 && (
            <p className="rounded-2xl border border-dashed border-line bg-white p-10 text-center text-sm text-slate-500">
              No jobs yet. Once a poster picks you, the offer lands here.
            </p>
          )}
        </ul>
      )}

      {/* --- Applications ---------------------------------------------------- */}
      {tab === 'applications' && (
        <ul className="space-y-4">
          {applications.data?.items.map((application) => (
            <li
              key={application.id}
              className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-line bg-white p-5 shadow-soft"
            >
              <div>
                <h2 className="text-base font-bold text-ink">{application.job.title}</h2>
                <p className="mt-1 text-xs text-slate-500">{application.job.posterName}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge tone={TONES[application.status] ?? 'neutral'}>
                  {label(application.status)}
                </Badge>
                <MessageButton
                  jobId={application.job.id}
                  personId={application.job.posterId}
                  label="Message"
                />
                {application.status !== 'ACCEPTED' && (
                  <Button
                    variant="ghost"
                    disabled={withdraw.isPending}
                    onClick={() =>
                      void run(() => withdraw.mutateAsync(application.id), 'Application withdrawn.')
                    }
                  >
                    Withdraw
                  </Button>
                )}
              </div>
            </li>
          ))}
          {applications.data?.items.length === 0 && (
            <p className="rounded-2xl border border-dashed border-line bg-white p-10 text-center text-sm text-slate-500">
              You have not applied to anything yet.{' '}
              <Link to="/jobs" className="font-semibold text-brand-600 hover:underline">
                Find a job
              </Link>
            </p>
          )}
        </ul>
      )}

      {/* --- Guardian permissions -------------------------------------------- */}
      {tab === 'consent' && (
        <ul className="space-y-4">
          {consents.data?.items.map((request) => (
            <li
              key={request.id}
              className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-line bg-white p-5 shadow-soft"
            >
              <div>
                <h2 className="text-base font-bold text-ink">{request.job.title}</h2>
                <p className="mt-1 text-xs text-slate-500">
                  {request.status === 'PENDING'
                    ? 'Waiting on your guardian'
                    : `Answered ${request.respondedAt?.slice(0, 10) ?? ''}`}
                </p>
              </div>
              <Badge tone={TONES[request.status] ?? 'neutral'}>{label(request.status)}</Badge>
            </li>
          ))}
          {consents.data?.items.length === 0 && (
            <p className="rounded-2xl border border-dashed border-line bg-white p-10 text-center text-sm text-slate-500">
              No permission requests yet.
            </p>
          )}
        </ul>
      )}
    </TeenShell>
  );
}
